import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';
import SectionHeading from '../../components/base/SectionHeading';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { attachTilt } from '../../hooks/useTiltCards';
import { timeline, team } from '../../mocks/portfolio';

gsap.registerPlugin(ScrollTrigger);

function TimelineSection() {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      const items = el.querySelectorAll<HTMLElement>('.timeline-item');
      items.forEach((item) => {
        gsap.fromTo(
          item,
          { x: -40, opacity: 0, filter: 'blur(14px)' },
          {
            x: 0,
            opacity: 1,
            filter: 'blur(0px)',
            duration: 1.4,
            ease: 'power3.out',
            scrollTrigger: { trigger: item, start: 'top 88%' },
          }
        );
      });
      const line = el.querySelector('.timeline-line');
      if (line) {
        gsap.fromTo(
          line,
          { scaleY: 0 },
          {
            scaleY: 1,
            transformOrigin: 'top center',
            ease: 'none',
            scrollTrigger: {
              trigger: el,
              start: 'top 60%',
              end: 'bottom 80%',
              scrub: true,
            },
          }
        );
      }
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={ref} className="relative bg-[#050505] py-24 md:py-32">
      <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16">
        <SectionHeading
          eyebrow="Timeline"
          title="Seven years of conviction."
          description="A short history of how Vex came to be — milestones, mistakes, and the moments we doubled down."
          className="mb-20"
        />

        <div className="relative pl-10 md:pl-16">
          <div className="absolute left-2 md:left-4 top-2 bottom-2 w-px bg-white/10">
            <div className="timeline-line absolute inset-0 w-px bg-gradient-to-b from-white via-white/40 to-transparent" />
          </div>

          {timeline.map((t) => (
            <div key={t.year} className="timeline-item relative pb-14 last:pb-0 group">
              <div className="absolute -left-10 md:-left-16 top-1 w-5 h-5 rounded-full liquid-glass flex items-center justify-center">
                <span className="w-1.5 h-1.5 rounded-full bg-white" />
              </div>
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-white/40 mb-3">
                {t.year}
              </div>
              <h3 className="font-display text-3xl md:text-4xl mb-3 cursor-default">
                <span className="title-shimmer inline-block">{t.title}</span>
              </h3>
              <p className="text-white/65 max-w-xl leading-relaxed">{t.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ValuesSection() {
  const revealRef = useScrollReveal<HTMLDivElement>({ stagger: 0.1 });
  const tiltRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!tiltRef.current) return;
    return attachTilt(tiltRef.current, 4);
  }, []);

  const values = [
    { metric: 'No.', value: '01', label: 'Founder-first', detail: 'We never compete with our companies. Always on the same side of the table.' },
    { metric: 'No.', value: '02', label: 'Long horizons', detail: 'Frontier tech compounds slowly, then suddenly. We are not in a hurry.' },
    { metric: 'No.', value: '03', label: 'Technical depth', detail: 'Every partner has shipped product. We can read your stack and your model card.' },
    { metric: 'No.', value: '04', label: 'Quiet capital', detail: 'No noise, no theater. Just rigorous diligence and steady support.' },
  ];

  return (
    <section ref={revealRef} className="relative bg-[#070709] py-24 md:py-32 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <SectionHeading
          eyebrow="What we value"
          title="The way we work, in four lines."
          className="mb-16"
        />
        <div ref={tiltRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((v) => (
            <div
              key={v.value}
              className="gsap-reveal tilt-card hover-glow glass-card rounded-2xl p-8 min-h-[260px] flex flex-col"
            >
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-white/30 mb-6">
                {v.metric} {v.value}
              </div>
              <div className="mt-auto">
                <h3 className="font-display text-2xl mb-3">
                  <span className="title-shimmer inline-block">{v.label}</span>
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">{v.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TeamSection() {
  const revealRef = useScrollReveal<HTMLDivElement>({ stagger: 0.12 });
  const tiltRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!tiltRef.current) return;
    return attachTilt(tiltRef.current, 5);
  }, []);

  return (
    <section ref={revealRef} className="relative bg-[#050505] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <SectionHeading
          eyebrow="People"
          title="Operators behind the firm."
          description="Vex is run by a small team of practitioners — founders, engineers, and researchers who treat investing as a craft."
          className="mb-16"
        />
        <div ref={tiltRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {team.map((p) => (
            <article key={p.name} className="gsap-reveal tilt-card group">
              <div className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-white/10 hover-glow">
                <img
                  src={p.image}
                  alt={`${p.name}, ${p.role}`}
                  title={`${p.name} - ${p.role}`}
                  className="absolute inset-0 w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 tilt-lift">
                  <div className="font-display text-xl">
                    <span className="title-shimmer inline-block">{p.name}</span>
                  </div>
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mt-1">
                    {p.role}
                  </div>
                </div>
              </div>
              <p className="mt-4 text-sm text-white/60 leading-relaxed">{p.bio}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function StoryPage() {
  return (
    <PageLayout transparentNav>
      <PageHero
        eyebrow="Our story"
        title="A small firm with long horizons."
        description="Vex is an angel collective backing frontier technology — quietly, patiently, and with deep conviction. We are not a brand, we are an instrument."
        image="https://readdy.ai/api/search-image?query=Cinematic%20wide%20composition%20of%20a%20warm%20golden%20horizon%20line%20diffusing%20through%20soft%20foggy%20charcoal%20atmosphere%20with%20faint%20particle%20mist%2C%20deep%20negative%20space%2C%20editorial%20technology%20magazine%20aesthetic%2C%20premium%20minimalist%20background&width=1800&height=900&seq=vex-story-hero&orientation=landscape"
      />
      <ValuesSection />
      <TimelineSection />
      <TeamSection />
    </PageLayout>
  );
}